import { useCallback, useMemo } from 'react';

import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';

import type { DateRange, NonEmptyDateRange } from '@packages/fenrir/components';
import { useDateRangeQueryParams } from '@packages/hooks/query-params/use-date-range-query-params/use-date-range-query-params';
import {
  DatePickerShortcuts,
  dateRangeGenerators,
} from '@packages/hooks/use-date-picker-shortcuts/use-date-picker-shortcuts';

export type FlowDataFilters = {
  dateRange: NonEmptyDateRange<Dayjs>;
};

export type SetFlowDataFilters = {
  dateRange: DateRange<Dayjs>;
};

const generateDefaultDateRange = () =>
  dateRangeGenerators[DatePickerShortcuts.Past24](dayjs());

export const useFlowDataFilterParams = () => {
  const [dateRangeQueryParams, setDateRangeQueryParams] =
    useDateRangeQueryParams(generateDefaultDateRange(), 'start', 'end');

  const flowDataFilterParams = useMemo<FlowDataFilters>(() => {
    const {
      dateRange: [startDate, endDate],
    } = dateRangeQueryParams;

    const dateRange: NonEmptyDateRange<Dayjs> =
      startDate === null || endDate === null
        ? generateDefaultDateRange()
        : [startDate, endDate];

    return {
      dateRange,
    };
  }, [dateRangeQueryParams]);

  const setFlowDataFilterParams = useCallback(
    ({ dateRange }: SetFlowDataFilters) => {
      setDateRangeQueryParams({
        dateRange,
      });
    },
    [setDateRangeQueryParams],
  );

  return { flowDataFilterParams, setFlowDataFilterParams };
};
